import { useState } from "react";

interface RevenueData {
  month: string;
  revenue: number;
  profit: number;
  expenses: number;
}

interface RevenueChartProps {
  revenueByMonth: { month: string; revenue: number }[];
}

type Metric = "revenue" | "profit" | "expenses";

const METRIC_CONFIG: Record<Metric, { label: string; color: string; bgColor: string; borderColor: string }> = {
  revenue: { label: "Revenue", color: "#00D4FF", bgColor: "bg-electric-blue/10", borderColor: "border-electric-blue/30" },
  profit: { label: "Profit", color: "#00FF88", bgColor: "bg-cyber-green/10", borderColor: "border-cyber-green/30" },
  expenses: { label: "Expenses", color: "#FF6B35", bgColor: "bg-cyber-orange/10", borderColor: "border-cyber-orange/30" },
};

export default function RevenueChart({ revenueByMonth }: RevenueChartProps) {
  const [activeMetric, setActiveMetric] = useState<Metric>("revenue");
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [range, setRange] = useState<"6m" | "12m">("12m");

  const fullData: RevenueData[] = revenueByMonth.map((m) => ({
    month: m.month,
    revenue: m.revenue,
    profit: m.revenue * 0.3,
    expenses: m.revenue * 0.7,
  }));
  const data = range === "6m" ? fullData.slice(-6) : fullData;
  const values = data.map((d) => d[activeMetric as keyof RevenueData] as number);
  const maxVal = Math.max(...values);
  const minVal = Math.min(...values);
  const totalRevenue = fullData.reduce((s, d) => s + d.revenue, 0);
  const totalProfit = fullData.reduce((s, d) => s + d.profit, 0);
  const totalExpenses = fullData.reduce((s, d) => s + d.expenses, 0);
  const currentMetric = METRIC_CONFIG[activeMetric];

  const getBarHeight = (val: number) => {
    const range2 = maxVal - minVal || 1;
    return Math.max(8, ((val - minVal) / range2) * 100);
  };

  const formatK = (n: number) => `$${(n / 1000).toFixed(1)}k`;

  const growthPct = data.length >= 2
    ? (((data[data.length - 1] as any)[activeMetric] - (data[0] as any)[activeMetric]) / (data[0] as any)[activeMetric] * 100).toFixed(1)
    : "0";

  return (
    <div className="bg-dark-card border border-dark-border rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 py-4 border-b border-dark-border">
        <div>
          <h2 className="text-white font-bold text-sm" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Revenue Overview
          </h2>
          <p className="text-silver-dim text-xs mt-0.5">Financial performance over time</p>
        </div>
        <div className="flex items-center gap-2">
          {(["6m", "12m"] as const).map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                range === r
                  ? "bg-electric-blue/20 text-electric-blue border border-electric-blue/30"
                  : "text-silver hover:text-white border border-dark-border"
              }`}
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-3 divide-x divide-dark-border border-b border-dark-border">
        {(Object.entries(METRIC_CONFIG) as [Metric, typeof METRIC_CONFIG[Metric]][]).map(([key, cfg]) => {
          const total = key === "revenue" ? totalRevenue : key === "profit" ? totalProfit : totalExpenses;
          return (
            <button
              key={key}
              onClick={() => setActiveMetric(key)}
              className={`flex flex-col gap-0.5 px-4 py-3 text-left transition-all cursor-pointer ${
                activeMetric === key ? cfg.bgColor : "hover:bg-dark-panel"
              }`}
            >
              <span className="text-silver-dim text-xs">{cfg.label}</span>
              <span
                className="text-lg font-black"
                style={{ fontFamily: "'Space Grotesk', sans-serif", color: activeMetric === key ? cfg.color : "#9CA3AF" }}
              >
                {formatK(total)}
              </span>
              {activeMetric === key && (
                <span className="text-xs font-semibold" style={{ color: cfg.color }}>
                  +{growthPct}% over period
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Chart */}
      <div className="px-5 pt-5 pb-4">
        <div className="flex items-end gap-1.5 h-40">
          {data.map((d, i) => {
            const val = (d as any)[activeMetric];
            const barH = getBarHeight(val);
            const isHovered = hoveredIndex === i;
            return (
              <div
                key={d.month}
                className="flex-1 flex flex-col items-center gap-1 group cursor-pointer"
                onMouseEnter={() => setHoveredIndex(i)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                {/* Tooltip */}
                <div
                  className={`transition-all duration-150 ${isHovered ? "opacity-100 scale-100" : "opacity-0 scale-95 pointer-events-none"}`}
                >
                  <div className="bg-dark-panel border border-dark-border rounded-lg px-2 py-1 text-center whitespace-nowrap mb-1">
                    <p className="text-white text-xs font-bold">{formatK(val)}</p>
                    <p className="text-silver-dim text-xs">{d.month}</p>
                  </div>
                </div>
                {/* Bar */}
                <div className="w-full flex items-end" style={{ height: "120px" }}>
                  <div
                    className="w-full rounded-t-md transition-all duration-300"
                    style={{
                      height: `${barH}%`,
                      background: isHovered
                        ? currentMetric.color
                        : `${currentMetric.color}55`,
                      boxShadow: isHovered ? `0 0 12px ${currentMetric.color}66` : "none",
                    }}
                  />
                </div>
                <span className="text-silver-dim text-xs">{d.month}</span>
              </div>
            );
          })}
        </div>

        {/* Y-axis labels */}
        <div className="flex justify-between mt-2 pt-2 border-t border-dark-border">
          <span className="text-silver-dim text-xs">{formatK(minVal)}</span>
          <span className="text-silver-dim text-xs">{formatK((maxVal + minVal) / 2)}</span>
          <span className="text-silver-dim text-xs">{formatK(maxVal)}</span>
        </div>
      </div>
    </div>
  );
}
